import { computed, Ref, ref, watch, watchEffect } from 'vue';
import { useBooks as useContents } from '@ilteducation/books';
import type { Content } from '@ilteducation/content-registry-types';
import useSimilarContentsStore from '@ilteducation/similar-content/src/similar-contents-store';
import type { ContentItem, RelatedContentItem } from '@ilteducation/similar-content/src/types';
import { storeToRefs } from 'pinia';

export type RelatedContent<T extends Content> = T & {
  item: RelatedContentItem;
};

const useSimilarContentContext = <T extends Content>(
  contextKey: Ref<string>,
  contentIds: Ref<string[]>,
  contentItemType?: string,
) => {
  const similarContentsStore = useSimilarContentsStore();
  const { isLoadingByContextKey } = storeToRefs(similarContentsStore);
  const { getById } = useContents();

  const relatedContentItems = computed(() =>
    (similarContentsStore.similarContentItemsByContextKey[contextKey.value] ?? []).filter((i) => i.distance < 1),
  );
  const isLoading = computed(() => isLoadingByContextKey.value[contextKey.value]);
  const contentsById = ref<Record<string, T>>({});
  const isContentLoading = ref<Record<string, boolean>>({});

  const loadContent = async (id: string) => {
    if (isContentLoading.value[id]) {
      return;
    }
    isContentLoading.value[id] = true;
    const content = (await getById(id)) as T | undefined; // TODO should refactor "useBooks" / book store into generic content
    if (content) {
      contentsById.value = { ...contentsById.value, [content.id]: content };
      isContentLoading.value[id] = false;
    }
  };

  const contentIdFromContentItem = (item: ContentItem): string => {
    if (item.type === 'BookPart') {
      return item.metadata!.bookId as string;
    }

    return item.id;
  };

  watchEffect(() => {
    relatedContentItems.value.forEach((item) => {
      const id = contentIdFromContentItem(item);
      if (contentsById.value[id] === undefined && !isContentLoading.value[id]) {
        loadContent(id);
      }
    });
  });

  const relatedContents = computed<RelatedContent<T>[]>(() =>
    relatedContentItems.value
      .filter((item) => contentsById.value[contentIdFromContentItem(item)] !== undefined)
      .map((item) => {
        const originalContent = contentsById.value[contentIdFromContentItem(item)];
        // copy as we mutate, and some content-items might point to the same conte.t, eg in case of "book parts"
        const content = JSON.parse(JSON.stringify(originalContent));
        content.item = item;

        return content as RelatedContent<T>;
      }),
  );

  watch(
    () => [contentIds.value, contextKey.value],
    async () => {
      if (
        !isLoadingByContextKey.value[contextKey.value] &&
        (similarContentsStore.similarContentItemsByContextKey[contextKey.value] ?? []).length === 0
      ) {
        isLoadingByContextKey.value[contextKey.value] = true;
        try {
          await similarContentsStore.loadMultiple(contentIds.value, contextKey.value, contentItemType);
        } finally {
          isLoadingByContextKey.value[contextKey.value] = false;
        }
      }
    },
    { immediate: true },
  );

  return { relatedContents, isLoading };
};

export default useSimilarContentContext;
